import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";

export enum CustomerCustomFieldDataType {
  STRING = "string",
  NUMBER = "number",
  BOOLEAN = "boolean",
  DATE = "date",
}

export const customerCustomFieldValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.date(),
]);
export type CustomerCustomFieldValue = z.infer<
  typeof customerCustomFieldValueSchema
>;

const base = {
  _id: zExt.objectId(),
  name: z.string(),
  isMultiValued: z.boolean(),
  createdAt: z.date().nullish(),
  updatedAt: z.date().nullish(),
};

export const customerCustomFieldDeclarationSchema = z.union([
  z.object({
    ...base,
    dataType: z.literal(CustomerCustomFieldDataType.STRING),
    options: z.array(z.string()).nullish(),
  }),
  z.object({
    ...base,
    dataType: z.literal(CustomerCustomFieldDataType.NUMBER),
    options: z.array(z.number()).nullish(),
  }),
  z.object({
    ...base,
    dataType: z.literal(CustomerCustomFieldDataType.BOOLEAN),
    options: z.array(z.boolean()).nullish(),
  }),
  z.object({
    ...base,
    dataType: z.literal(CustomerCustomFieldDataType.DATE),
    options: z.array(z.date()).nullish(),
  }),
]);
export type CustomerCustomFieldDeclaration = z.infer<
  typeof customerCustomFieldDeclarationSchema
>;

export const customerCustomFieldDeclarationSetSchema = z.object({
  _id: zExt.objectId(),
  teamId: zExt.objectId(),
  declarations: z.array(customerCustomFieldDeclarationSchema),
});
/**
 * A schema for defining schemas. The most concrete code ever written.
 * Named a "declaration" as an analogy for declarations in programming (stating something's name and type),
 * and because it avoids naming ambiguity with the terms "schema" and "definition", which already have many meanings in Redo's codebase.
 */
export type CustomerCustomFieldDeclarationSet = z.infer<
  typeof customerCustomFieldDeclarationSetSchema
>;

/**
 * Upper bound on the size of a merchant-curated `options` picklist.
 *
 * Historical context: the public customer-profile-update endpoint used to
 * `$addToSet` every incoming string value onto `declarations.options` so the
 * segmentation dropdown would show "values seen so far." That bloated the
 * array to tens of thousands of entries for high-cardinality fields (e.g.
 * `email`), and the array got returned from every dropdown fetch. The ingest
 * path no longer writes to `options`, but existing declarations may still
 * carry bloated arrays.
 *
 * This ceiling is used as a read-side guardrail: any declaration whose
 * `options` is longer than this is treated as NOT a merchant-curated
 * picklist, and downstream read paths (segmentation dropdown, storefront
 * form dropdowns) fall through to their non-curated behavior rather than
 * rendering the bloated list.
 */
export const CURATED_OPTIONS_MAX = 500;

/**
 * Returns true when the declaration carries a merchant-curated `options`
 * picklist of reasonable size. See `CURATED_OPTIONS_MAX` for rationale.
 */
export function hasCuratedOptions(
  declaration: Pick<CustomerCustomFieldDeclaration, "options">,
): boolean {
  return (
    Array.isArray(declaration.options) &&
    declaration.options.length > 0 &&
    declaration.options.length <= CURATED_OPTIONS_MAX
  );
}

export const customerCustomFieldInstanceSchema = z.object({
  declarationId: zExt.objectId(),
  dataType: createEnum(CustomerCustomFieldDataType, {
    enumName: "CustomerCustomFieldDataType",
  }), // never changes, so it's safe and useful to denormalize
  values: z.array(customerCustomFieldValueSchema),
  createdAt: z.date().nullish(),
  updatedAt: z.date().nullish(),
});
export type CustomerCustomFieldInstance = z.infer<
  typeof customerCustomFieldInstanceSchema
>;
